import React from 'react';
import Spinner from '../Spinner/Spinner';
import { WithNative } from '../../../types/DesignSystem/native';
import { withClassName } from '../cn';

/**
 * The "rows are on screen, new ones are on the way" state.
 *
 * ## Why it is its own component
 *
 * It was inline in `Table.tsx`, which is fine until a screen paginates a table the DS
 * `Table` does not render. Catalog Management's seven Data Review tables are
 * hand-rolled `<table>`s — they need `colgroup` widths and a split header/body scroll
 * pair that `Table` has no API for yet — so they had a refetch with **no refresh
 * indicator at all**: the rows just dimmed. Guide/ISR, which does use `Table`, showed
 * the chip. Same product, same gesture, two answers.
 *
 * Copying the markup would have been the fourth "one implementation, two copies"
 * incident recorded in this repo (`emitsCss`, the token resolver, the paginator). So
 * `Table` renders this and so does anything else that needs it.
 *
 * ## The two things about it that are deliberate
 *
 * **It is a REFRESH state, not a load state.** A first load has nothing to dim, so the
 * caller shows skeletons or a spinner instead and leaves `active` false. `Table` used
 * to draw this overlay for any `loading`, which put "Refreshing" on top of the empty
 * state's "No data available" — two contradictory answers at once.
 *
 * **The overlay itself paints nothing** (§ 59). It was `tw-bg-white tw-bg-opacity-75`,
 * a white wash that turned a dark table into a pale blob — the § 54.3 gap. Roles carry
 * no alpha, so the surface below dims itself and the page's own themed ground shows
 * through; this element only positions the chip and blocks clicks on stale rows. The
 * chip IS self-grounded, because its label has to stay legible over faded content.
 *
 * The parent must be `position: relative`.
 */
export interface RefreshOverlayOwnProps {
  /** Content is already on screen and a refresh is in flight. */
  active: boolean;
  /** Overridable for i18n. Also the accessible announcement. */
  label?: string;
  className?: string;
  'data-testid'?: string;
}

export type RefreshOverlayProps = WithNative<
  RefreshOverlayOwnProps,
  React.HTMLAttributes<HTMLDivElement>
>;

const RefreshOverlay = React.forwardRef<HTMLDivElement, RefreshOverlayProps>(
  function RefreshOverlay({
    active,
    label = 'Refreshing',
    className = '',
    'data-testid': dataTestId,
    ...rest
  }, ref) {
    if (!active) return null;

    return (
      <div
        /* `rest` FIRST, as everywhere in this design system: spread last, a caller's
           stray prop would silently replace something the component must own — here
           `role="status"`, which is the only reason a screen-reader user learns the
           table is reloading. */
        {...rest}
        ref={ref}
        role="status"
        data-testid={dataTestId}
        className={withClassName(`tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-10`, className)}
      >
        <div className="tw-bg-surface tw-border tw-border-edge tw-rounded-srf tw-shadow-ds-sm tw-px-4 tw-py-2">
          <Spinner size="md" label={label} showLabel />
        </div>
      </div>
    );
  },
);

export default RefreshOverlay;
